import React, { useEffect, useRef, useState } from 'react';
import { X, Brain, PauseCircle, Trash2 } from 'lucide-react';

/**
 * SaveModeModal — 3-way learning decision for closing / archiving a
 * position. Presents an exit-price input and three action buttons:
 *
 *   • Save Data to Learning    → feeds the learning + truth pipelines
 *   • Save as Unfinished Data  → preserved for review, isolated from stats
 *   • Discard (Don't Save)     → closed and hidden from every metric
 *
 * Props
 * -----
 *   open, onClose
 *   symbol            : string     — position ticker (title)
 *   defaultExit       : number     — current best exit price (live quote)
 *   requireExit       : boolean    — force operator to type a price (close)
 *                                    or use live quote (archive/remove).
 *   mode              : "close" | "archive"  — copy tweaks only.
 *   onSubmit(payload) : ({ exit, save_mode }) => Promise<void>
 */
export default function SaveModeModal({
  open, onClose, symbol = '', defaultExit = '', requireExit = true,
  mode = 'close', onSubmit,
}) {
  const [exit, setExit] = useState(defaultExit ?? '');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setExit(defaultExit ?? '');
      setBusy(false);
      // Autofocus the exit input a tick after mount so keyboard flow works.
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open, defaultExit]);

  if (!open) return null;

  const submit = async (save_mode) => {
    const exitNum = requireExit ? parseFloat(exit) : (parseFloat(exit) || null);
    if (requireExit && (isNaN(exitNum) || exitNum <= 0)) {
      inputRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ exit: exitNum, save_mode });
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const isClose = mode === 'close';

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={onClose}
      data-testid="save-mode-modal"
    >
      <div
        className="w-[min(520px,92vw)] border border-cyan-sys bg-[var(--bg-panel)] shadow-[0_0_40px_rgba(0,229,255,0.15)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--border-default)] px-4 py-2">
          <div className="text-cyan-sys text-[10px] tracking-[0.35em] uppercase font-display">
            {isClose ? '// close position' : '// remove position'} — {symbol}
          </div>
          <button
            data-testid="save-mode-close"
            onClick={onClose}
            className="text-[var(--txt-muted)] hover:text-sell"
            aria-label="cancel"
          >
            <X size={14} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {isClose && (
            <label className="block">
              <span className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys">exit price</span>
              <input
                ref={inputRef}
                data-testid="save-mode-exit"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                value={exit}
                onChange={(e) => setExit(e.target.value)}
                disabled={busy}
                placeholder="e.g. 187.42"
                className="mt-1 w-full bg-black border border-[var(--border-muted)] focus:border-cyan-sys outline-none px-2 py-1.5 font-mono text-[14px] text-cyan-sys"
              />
            </label>
          )}

          <p className="text-[11px] text-[var(--txt-secondary)] leading-relaxed">
            Choose how this trade should feed the learning &amp; truth pipelines.
            You can review or delete the row later from the Portfolio archive.
          </p>

          <div className="grid grid-cols-1 gap-2 pt-1">
            <button
              data-testid="save-mode-learning"
              onClick={() => submit('learning')}
              disabled={busy}
              className="group flex items-start gap-3 text-left border border-buy hover:bg-buy hover:text-black px-3 py-2 disabled:opacity-40"
            >
              <Brain size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[11px] tracking-[0.25em] uppercase font-display text-buy group-hover:text-black">
                  save data to learning
                </div>
                <div className="text-[10px] opacity-80 group-hover:text-black">
                  Adds to trade_outcomes, adjusts weights, appears in Truth. Default.
                </div>
              </div>
            </button>

            <button
              data-testid="save-mode-unfinished"
              onClick={() => submit('unfinished')}
              disabled={busy}
              className="group flex items-start gap-3 text-left border border-hold hover:bg-hold hover:text-black px-3 py-2 disabled:opacity-40"
            >
              <PauseCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[11px] tracking-[0.25em] uppercase font-display text-hold group-hover:text-black">
                  save as unfinished data
                </div>
                <div className="text-[10px] opacity-80 group-hover:text-black">
                  Closed early. Kept for your review, but isolated from learning + truth metrics.
                </div>
              </div>
            </button>

            <button
              data-testid="save-mode-discard"
              onClick={() => submit('discard')}
              disabled={busy}
              className="group flex items-start gap-3 text-left border border-sell hover:bg-sell hover:text-black px-3 py-2 disabled:opacity-40"
            >
              <Trash2 size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="text-[11px] tracking-[0.25em] uppercase font-display text-sell group-hover:text-black">
                  don't save (discard)
                </div>
                <div className="text-[10px] opacity-80 group-hover:text-black">
                  Treated as incomplete. Zero impact on any metric or dashboard.
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
